import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Phone, MessageCircle, Check } from "lucide-react";
import { db } from "../../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { Button } from "../ui/Button";

export default function OrderStatus() {
  const navigate = useNavigate();
  const { jobId } = useParams();

  // inside your component
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [taskerPhone, setTaskerPhone] = useState(""); 
  

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tasker, setTasker] = useState(null);

  // 🔴 Real-time listener
  useEffect(() => {
    if (!jobId) return;

    const unsubscribe = onSnapshot(
      doc(db, "jobs", jobId),
      (snap) => {
        if (snap.exists()) {
          setJob({ id: snap.id, ...snap.data() });
        }
        setLoading(false);
      },
      (err) => {
        console.error("OrderStatus listener error:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [jobId]);

  useEffect(() => {
    if (!job?.taskerId) return;

    const unsub = onSnapshot(
      doc(db, "taskers", job.taskerId),
      (snap) => {
        if (snap.exists()) {
          setTasker({ id: snap.id, ...snap.data() });
          setTaskerPhone(snap.data().phone || "");
        } 
      }
    );

    return () => unsub();
  }, [job?.taskerId]); 
  
  const handleCallClick = async () => {
    if (!taskerPhone) return;

    if (phoneRevealed) {
      window.location.href = `tel:${taskerPhone}`;
      return;
    }

    setPhoneRevealed(true);

    try {
      await navigator.clipboard.writeText(taskerPhone);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };
  
  // 🧠 Derived UI State
  const uiState = useMemo(() => {
    if (!job) return "loading";

    if (["draft", "pending", "open"].includes(job.status))
      return "not_confirmed";

    if (job.status === "cancelled") return "cancelled";

    if (job.status === "completed") return "completed";

    if (job.status === "assigned") {
      return job.progressStatus || "confirmed";
    }

    return "not_confirmed";
  }, [job]);

  // 🟢 Progress Steps
  const steps = [
    { key: "confirmed", label: "Confirmed" },
    { key: "on the way", label: "On The Way" },
    { key: "arrived", label: "Arrived" },
    { key: "completed", label: "Completed" },
  ];

  const activeIndex = steps.findIndex((s) => s.key === uiState);

  const progressPercent =
    activeIndex >= 0 ? (activeIndex / (steps.length - 1)) * 100 : 0;

  // 🟢 Review allowed within 24 hours
  const canReview = useMemo(() => {
    if (!job || job.status !== "completed" || !job.completedAt) return false;

    const completedTime = job.completedAt.toDate().getTime();
    return Date.now() - completedTime <= 24 * 60 * 60 * 1000;
  }, [job]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Job not found.
      </div>
    );
  }

  // 🧾 Status Title + Subtitle
  let title = "";
  let subtitle = "";

  switch (uiState) {
    case "not_confirmed":
      title = "Not Confirmed Yet";
      subtitle = "Waiting for tasker confirmation";
      break;

    case "confirmed":
      title = "Confirmed";
      subtitle = "Your tasker confirmed the booking";
      break;

    case "on the way":
      title = "On The Way";
      subtitle = "Your tasker is on the way";
      break;

    case "arrived":
      title = "Arrived";
      subtitle = "Your tasker has arrived";
      break;

    case "completed":
      title = "Completed";
      subtitle = "Job completed successfully";
      break;

    case "cancelled":
      title = "Cancelled";
      subtitle = "This job was cancelled";
      break;

    default:
      title = "Status";
      subtitle = "";
  }

  const isCancelled = uiState === "cancelled";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 text-center">
          <h2 className="text-[#1E3A8A]">Job Status</h2>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Status Card */}
        <div className="bg-white rounded-lg p-6 shadow-sm mb-6">

          {/* Icon */}
          <div className="text-center mb-6">
            <div
              className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 ${
                isCancelled ? "bg-red-100" : "bg-green-100"
              }`}
            >
              <span className="text-5xl">🔧</span>
            </div>

            <h3 className="text-[#1E3A8A] mb-2">{title}</h3>
            <p className={`${isCancelled ? "text-red-600" : "text-gray-600"}`}>
              {subtitle}
            </p>
          </div>

          {/* Progress Bar */}
          {uiState !== "not_confirmed" && (
            <div className="relative mb-8">
              <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200">
                <div
                  className={`h-full transition-all ${
                    isCancelled ? "bg-red-500" : "bg-[#006C35]"
                  }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <div className="relative flex justify-between">
                {steps.map((step, index) => {
                  const active = index <= activeIndex;

                  return (
                    <div key={step.key} className="flex flex-col items-center">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                          active
                            ? isCancelled
                              ? "bg-red-500 text-white"
                              : "bg-[#006C35] text-white"
                            : "bg-white border-2 border-gray-300 text-gray-400"
                        }`}
                      >
                        {active ? "✓" : index + 1}
                      </div>
                      <span className="text-xs text-gray-600 text-center">
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tasker Card */}
          {job.status === "assigned" && tasker && (
            <div className="flex items-center gap-4 bg-gray-50 rounded-lg p-4 mb-6">
              
              {/* Avatar */}
              <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200">
                {tasker.imageUrl ? (
                  <img
                    src={tasker.imageUrl}
                    alt={tasker.businessName || "Tasker"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    👷
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex flex-col">
                <span className="text-[#1E3A8A] font-medium">
                  {tasker.businessName || tasker.name || "Tasker"}
                </span>
                <span className="text-sm text-gray-500">
                  ETA: Unknown
                </span>
              </div>

            </div>
          )}

          {/* Actions (only if active job) */}
          {["assigned"].includes(job.status) && !isCancelled && (
            <div className="grid grid-cols-2 gap-4">
              <Button className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-[#006C35] text-[#006C35] rounded-lg hover:bg-green-50 transition-colors">
                <MessageCircle size={20} />
                Chat
              </Button>
              <Button
                className="py-6 bg-[#006C35] hover:bg-[#005428] text-white relative"
                onClick={handleCallClick}
                disabled={!taskerPhone}
              >
                <Phone size={18} className="mr-2" />

                <span className="truncate">
                  {phoneRevealed ? taskerPhone : "Call"}
                </span>

                {copied && (
                  <span className="absolute -top-1 -right-1 text-[10px] bg-white text-[#006C35] px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                    <Check size={10} />
                    Copied
                  </span>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Job Details */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-[#1E3A8A] mb-4">Job Details</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Service</span>
              <span className="text-[#1E3A8A]">
                {job.subcategoryId || "—"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date</span>
              <span className="text-[#1E3A8A]">
                {job.preferredDate || "—"} {job.preferredTime || ""}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Location</span>
              <span className="text-[#1E3A8A]">
                {job.locationText || "—"}
              </span>
            </div>
            <div className="flex justify-between pt-3 border-t border-gray-200">
              <span>Total Paid</span>
              <span className="text-[#006C35]">
                {job.price || 0} SAR
              </span>
            </div>
          </div>
        </div>

        {/* Review Button */}
        {canReview && (
          <button
            onClick={() => navigate(`/customer/review/${jobId}`)}
            className="w-full mt-6 bg-[#1E3A8A] text-white py-4 rounded-lg hover:bg-[#152a63] transition-colors"
          >
            Complete & Review
          </button>
        )}
      </div>
    </div>
  );
}